import { ALL_NAV_HREFS, menuItems, moduleHrefs } from './menu';

/**
 * Which nav row a given route lights up (M1 · 86d44z1g9, must-fix #1).
 *
 * Naive `pathname.startsWith(href)` is wrong in three separate ways, and the shell has shipped
 * with all three:
 *
 *  1. `href: '/'` is a prefix of EVERY route, so Home lights on every page and two rail buttons
 *     claim `aria-pressed="true"` at once. Today's `SidebarItem` special-cases `/` (:30-32); a
 *     rail written from scratch drops that guard silently.
 *  2. Prefix matching ignores segment boundaries, so `/downloads` would light a `/d` entry and
 *     `/mro/issuance/picklist-guide` would light a `/mro/issuance/picklist` entry.
 *  3. Four in-shell detail routes are nested UNDER no nav href at all and are therefore unlit
 *     today — the nav href is `…/picklists` (plural) while the routes are `…/picklist/:id`
 *     (singular), and the document viewer lives at a completely separate `/d/:docNo` root.
 *
 * The fix is one table plus longest-match. Every nav href implicitly matches itself; entries
 * below add the extra bases that should resolve to the same row.
 *
 * ⚠️ `/p/:token` is deliberately absent — it is the PUBLIC document route, rendered outside
 * AppLayout, so no rail exists to light.
 */
export const NAV_ROUTE_MATCHERS: Readonly<Record<string, readonly string[]>> = {
  // Nav href is plural; every picklist detail route is singular, and the creation wizard sits on
  // a third stem again. All three are "the Picklists screen" as far as the user is concerned.
  //   /mro/issuance/picklist/:id            → Picklists
  //   /mro/issuance/picklist/:id/add-items  → Picklists
  //   /mro/issuance/create                  → Picklists
  // `/mro/issuance/picklist` does NOT swallow `/mro/issuance/picklist-guide`, which is its own
  // nav row: the base only matches on a `/` boundary, and `-guide` is not one.
  '/mro/issuance/picklists': ['/mro/issuance/picklist', '/mro/issuance/create'],
  // The document viewer is a short public-ish permalink stem, not a child of /documents.
  // `/d` cannot swallow `/downloads` for the same boundary reason.
  '/documents': ['/d'],
};

/** True when `pathname` is `base` or sits beneath it on a segment boundary. */
function isUnder(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(base + '/');
}

/**
 * Resolve a browser pathname to the single nav href that should light up, or `null` when the
 * route belongs to no nav row (e.g. `/profile`, `/notifications`).
 *
 * Longest matching base wins, so a more specific row always beats a more general one.
 */
export function resolveLitHref(
  pathname: string,
  navHrefs: readonly string[] = ALL_NAV_HREFS
): string | null {
  // Home is EXACT. It is the whole reason prefix matching cannot be used unmodified.
  if (pathname === '/') return '/';

  let best: string | null = null;
  let bestLength = -1;

  for (const href of navHrefs) {
    if (href === '/') continue; // handled above; never let it act as a prefix
    const bases = [href, ...(NAV_ROUTE_MATCHERS[href] ?? [])];
    for (const base of bases) {
      if (isUnder(pathname, base) && base.length > bestLength) {
        best = href;
        bestLength = base.length;
      }
    }
  }

  return best;
}

/**
 * Which module owns a route, or `null` when none does (`/profile`, `/notifications`).
 *
 * Callers must treat `null` as "leave the current module alone" rather than "clear it" — landing on
 * the profile page should not blank the module panel behind it.
 */
export function resolveModuleId(pathname: string): string | null {
  const lit = resolveLitHref(pathname);
  if (lit === null) return null;
  const owner = menuItems.find((entry) => moduleHrefs(entry).includes(lit));
  return owner?.id ?? null;
}
